import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// Banner / Alert / DegradedBanner (design/components/banner.html). Persistent
// notices that stay until the user acts or state changes (unlike a transient
// Toast). Banner = page/section level; Alert = compact inline; DegradedBanner =
// full-bleed system bar in plain Spanish. Tone is icon + text, never bg colour
// alone; info/ok/warn use role=status (polite), errors use role=alert.

export type Tone = 'info' | 'ok' | 'warn' | 'sens'

function InfoIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 11v5M12 7.5h.01" />
    </svg>
  )
}
function CheckCircleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M8.5 12.5 11 15l4.5-5" />
    </svg>
  )
}
function WarnIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M10.3 3.1 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.1a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  )
}
function ErrorIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 8v5M12 16h.01" />
    </svg>
  )
}

const toneCfg: Record<Tone, { bg: string; border: string; ic: string; Icon: () => ReactNode }> = {
  info: {
    bg: 'bg-info-bg',
    border: 'border-[color-mix(in_srgb,var(--info)_25%,transparent)]',
    ic: 'text-info',
    Icon: InfoIcon,
  },
  ok: {
    bg: 'bg-ok-bg',
    border: 'border-[color-mix(in_srgb,var(--ok)_25%,transparent)]',
    ic: 'text-ok',
    Icon: CheckCircleIcon,
  },
  warn: {
    bg: 'bg-warn-bg',
    border: 'border-[color-mix(in_srgb,var(--warn)_25%,transparent)]',
    ic: 'text-warn',
    Icon: WarnIcon,
  },
  sens: {
    bg: 'bg-sens-bg',
    border: 'border-[color-mix(in_srgb,var(--sens)_25%,transparent)]',
    ic: 'text-sens',
    Icon: ErrorIcon,
  },
}

const richText = '[&_b]:font-semibold [&_b]:text-text [&_strong]:font-semibold [&_strong]:text-text'

export interface BannerProps {
  tone: Tone
  children: ReactNode
  action?: ReactNode
  dismissible?: boolean
  onDismiss?: () => void
  /** Override the tone-derived live region (sens → alert, else status). */
  role?: 'status' | 'alert'
  className?: string
}

export function Banner({
  tone,
  children,
  action,
  dismissible,
  onDismiss,
  role,
  className,
}: BannerProps) {
  const { t } = useTranslation('components')
  const [hidden, setHidden] = useState(false)
  const cfg = toneCfg[tone]
  const liveRole = role ?? (tone === 'sens' ? 'alert' : 'status')
  if (hidden) return null
  return (
    <div
      role={liveRole}
      className={cn(
        'flex w-full items-start gap-3 rounded-[11px] border px-[17px] py-[13px]',
        cfg.bg,
        cfg.border,
        className,
      )}
    >
      <span className={cn('flex-none pt-px [&_svg]:size-[17px]', cfg.ic)}>
        <cfg.Icon />
      </span>
      <div className={cn('flex-1 text-[12.5px] leading-normal text-text-2', richText)}>
        {children}
      </div>
      {action && <div className="flex-none self-center">{action}</div>}
      {dismissible && (
        <button
          type="button"
          aria-label={t('ui.banner.close')}
          onClick={() => {
            setHidden(true)
            onDismiss?.()
          }}
          className="flex size-6 flex-none items-center justify-center self-start rounded-md text-text-3 transition-colors hover:bg-black/[0.06] hover:text-text [&_svg]:size-[13px]"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            aria-hidden
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  )
}

export interface AlertProps {
  tone: Tone
  children: ReactNode
  className?: string
}

/** Compact inline notice — Banner's smaller sibling for in-form/in-card use;
 *  no action or dismiss. role=alert when tone is 'sens', role=status
 *  otherwise. */
export function Alert({ tone, children, className }: AlertProps) {
  const cfg = toneCfg[tone]
  return (
    <div
      role={tone === 'sens' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-[9px] rounded-md border px-3 py-2.5 text-[11.5px] leading-snug text-text-2',
        cfg.bg,
        cfg.border,
        richText,
        className,
      )}
    >
      <span className={cn('flex-none [&_svg]:size-[14px]', cfg.ic)}>
        <cfg.Icon />
      </span>
      <span>{children}</span>
    </div>
  )
}

// Solid status bars carry white text and must stay readable in BOTH themes, so
// they use theme-independent "solid" tokens, not the semantic --warn/--sens/--info
// tokens (which lighten in dark mode and would fail contrast under white text).
const degradedCfg = {
  warn: { bg: 'bg-[var(--warn-solid)]', role: 'status' as const, Icon: WarnIcon },
  sens: { bg: 'bg-[var(--sens-solid)]', role: 'alert' as const, Icon: ErrorIcon },
  info: { bg: 'bg-[var(--info-solid)]', role: 'status' as const, Icon: InfoIcon },
}

export interface DegradedBannerProps {
  severity: 'warn' | 'sens' | 'info'
  message: string
  onRetry?: () => void
  className?: string
}

export function DegradedBanner({ severity, message, onRetry, className }: DegradedBannerProps) {
  const { t } = useTranslation('components')
  const cfg = degradedCfg[severity]
  return (
    <div
      role={cfg.role}
      className={cn(
        'flex w-full items-center gap-[11px] rounded-[11px] px-[18px] py-[11px] text-[12.5px] font-medium text-white [&_svg]:size-[17px]',
        cfg.bg,
        className,
      )}
    >
      <span className="flex-none">
        <cfg.Icon />
      </span>
      <span>{message}</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="ml-auto flex-none rounded-sm border border-white/40 bg-black/[0.16] px-[11px] py-[5px] text-[11.5px] font-semibold text-white transition-colors hover:bg-black/[0.26]"
        >
          {t('ui.banner.retry')}
        </button>
      )}
    </div>
  )
}
